<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import AgentRepoDevcontainerStatus from './AgentRepoDevcontainerStatus.vue'
import AgentRepoDevcontainerActions from './AgentRepoDevcontainerActions.vue'
import AgentRepoCheckout from './AgentRepoCheckout.vue'
import AgentRepoPorts from './AgentRepoPorts.vue'
import AgentRepoProductOwner from './AgentRepoProductOwner.vue'
import AgentRepoTokenField from './AgentRepoTokenField.vue'
import { repoContainerBadge } from './AgentRepoStates'
import type { AgentRepoStatus } from '../stores/agent_repos'

// The detail panel beside the Repos table for the selected repo: its
// devcontainer (status, metrics, ports, lifecycle actions), the checkout's
// git state, its write-only tokens and the product-owner heartbeat. Built
// from the same AgentRepo* sections the per-repo cards used, so every field
// and action keeps its testid. Tokens are write-only: only "set / not set"
// is ever shown.
const props = defineProps<{
  repo: AgentRepoStatus
  allRepos: AgentRepoStatus[]
  latestCliVersion: string | null
}>()

const containerBadge = computed(() => repoContainerBadge(props.repo))

const CARD = 'flex flex-col gap-3 rounded-2xl border border-border-subtle bg-surface-raised p-4'
const DL = 'm-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-xs'
const presentClass = (present: boolean) => (present ? 'text-brand-primary' : 'text-status-alert')
const setClass = (set: boolean) => (set ? 'text-brand-primary' : 'text-ink-faint')
</script>

<template>
  <aside
    id="agent-repo-detail"
    :aria-label="`${repo.slug} details`"
    class="flex flex-col gap-4"
    :data-testid="`agent-repo-detail-${repo.slug}`"
  >
    <div class="flex flex-col gap-0.5">
      <h2 class="m-0 font-mono text-[15px] font-medium text-ink">{{ repo.slug }}</h2>
      <p v-if="repo.github_repo" class="m-0 font-mono text-xs text-ink-faint">{{ repo.github_repo }}</p>
    </div>

    <section :class="CARD" aria-labelledby="agent-repo-detail-devcontainer">
      <div class="flex items-center gap-2">
        <h3 id="agent-repo-detail-devcontainer" class="m-0 flex-1 text-sm font-semibold text-ink">Devcontainer</h3>
        <span
          class="rounded-full px-2 py-0.5 text-xs font-semibold"
          :class="containerBadge.pillClass"
          :data-testid="`agent-repo-detail-pill-${repo.slug}`"
        >
          {{ containerBadge.label }}
        </span>
      </div>
      <dl :class="DL">
        <dt class="text-ink-faint">Checkout</dt>
        <dd class="m-0" :class="presentClass(repo.checkout_exists)" :data-testid="`agent-repo-checkout-${repo.slug}`">
          {{ repo.checkout_exists ? 'Present' : 'Missing' }}
        </dd>
        <dt class="text-ink-faint">Devcontainer</dt>
        <dd
          class="m-0"
          :class="presentClass(repo.devcontainer_exists)"
          :data-testid="`agent-repo-devcontainer-${repo.slug}`"
        >
          {{ repo.devcontainer_exists ? 'Present' : 'Missing' }}
        </dd>
        <AgentRepoPorts :repo="repo" :all-repos="allRepos" />
        <AgentRepoDevcontainerStatus :repo="repo" :latest-cli-version="latestCliVersion" />
      </dl>
      <div class="flex flex-wrap items-center gap-2 border-t border-border-divider pt-3">
        <AgentRepoDevcontainerActions :repo="repo" />
      </div>
    </section>

    <section :class="CARD" aria-labelledby="agent-repo-detail-checkout">
      <h3 id="agent-repo-detail-checkout" class="m-0 text-sm font-semibold text-ink">Checkout</h3>
      <dl :class="DL">
        <AgentRepoCheckout :repo="repo" />
      </dl>
    </section>

    <section :class="CARD" aria-labelledby="agent-repo-detail-access">
      <h3 id="agent-repo-detail-access" class="m-0 text-sm font-semibold text-ink">Access tokens</h3>
      <dl :class="DL">
        <dt class="text-ink-faint">GitHub PAT</dt>
        <dd class="m-0" :class="setClass(repo.token_set)" :data-testid="`agent-repo-token-status-${repo.slug}`">
          {{ repo.token_set ? 'Set' : 'Not set' }}
        </dd>
        <dt class="text-ink-faint">Registry token</dt>
        <dd
          class="m-0"
          :class="setClass(repo.registry_token_set)"
          :data-testid="`agent-repo-registry-token-status-${repo.slug}`"
        >
          {{ repo.registry_token_set ? 'Set' : 'Not set' }}
        </dd>
      </dl>
      <AgentRepoTokenField
        :slug="repo.slug"
        param="github_token"
        label="GitHub PAT"
        :is-set="repo.token_set"
        placeholder="ghp_…"
        testid-prefix="agent-repo-token"
      />
      <AgentRepoTokenField
        :slug="repo.slug"
        param="registry_token"
        label="registry token"
        :is-set="repo.registry_token_set"
        placeholder="ghp_… (classic PAT with read:packages)"
        testid-prefix="agent-repo-registry-token"
        hint="Only needed when this repo's compose stack pulls a private ghcr.io image."
      />
    </section>

    <section :class="CARD" aria-labelledby="agent-repo-detail-po">
      <h3 id="agent-repo-detail-po" class="m-0 text-sm font-semibold text-ink">Product owner</h3>
      <p class="m-0 text-[13px] leading-normal text-ink-muted">
        Checks in on the repo on a computed cadence and proposes the next priority, within the ceilings below.
      </p>
      <AgentRepoProductOwner :repo="repo" />
      <RouterLink
        to="/admin/product-owner-runs"
        class="text-[13px] text-factory"
        :data-testid="`agent-repo-po-runs-link-${repo.slug}`"
        >All product-owner runs →</RouterLink
      >
    </section>
  </aside>
</template>
